/**
 * NL-041 shared recommendation contract.
 *
 * This module is deliberately client-safe and side-effect free. Callers provide
 * their own approved pricing snapshot; it does not read environment variables,
 * analytics, CRM, or provider state.
 */
export const PACKAGE_RECOMMENDATION_TIERS = ["Starter", "Growth", "Enterprise"] as const;

export type PackageRecommendationTier = (typeof PACKAGE_RECOMMENDATION_TIERS)[number];

export const PACKAGE_RECOMMENDATION_RULE_VERSION = "nl041-v1";

export const PACKAGE_COMPLEXITY_SIGNALS = [
  "agency-audience",
  "adult-audience",
  "franchise-audience",
  "white-label-service",
  "franchise-local-service",
  "compliance-addon",
] as const;

export type PackageComplexitySignal = (typeof PACKAGE_COMPLEXITY_SIGNALS)[number];

export type PackageRecommendationReason =
  | "baseline-scope"
  | "service-count-growth"
  | "service-count-enterprise"
  | "addon-count-growth"
  | "addon-count-enterprise"
  | "complexity-growth"
  | "complexity-enterprise"
  | "package-override"
  | PackageComplexitySignal;

export type PackageRecommendationMissingInformation =
  | "market"
  | "currency"
  | "pricing-snapshot"
  | "pricing-snapshot-version"
  | "pricing-band";

/**
 * A caller-owned, display-ready pricing snapshot. The resolver intentionally
 * does not determine currency conversion, price bands, or catalog authority.
 */
export type PackagePricingSnapshot = {
  readonly version: string;
  readonly bands: Readonly<Record<PackageRecommendationTier, string>>;
};

export type PackageRecommendationInput = {
  readonly audienceId?: string;
  readonly serviceIds?: readonly string[];
  readonly addOnIds?: readonly string[];
  readonly complexitySignals?: readonly PackageComplexitySignal[];
  readonly market?: string;
  readonly currency?: string;
  readonly packageOverride?: PackageRecommendationTier;
  readonly pricingSnapshot?: PackagePricingSnapshot;
};

export type PackageRecommendation = {
  readonly tier: PackageRecommendationTier;
  readonly reasons: readonly PackageRecommendationReason[];
  readonly pricingBand: string | null;
  readonly pricingSnapshotVersion: string | null;
  readonly ruleVersion: typeof PACKAGE_RECOMMENDATION_RULE_VERSION;
  readonly missingInformation: readonly PackageRecommendationMissingInformation[];
  readonly manualQuoteRequired: boolean;
  readonly overridden: boolean;
};

function isPackageRecommendationTier(value: unknown): value is PackageRecommendationTier {
  return typeof value === "string" && (PACKAGE_RECOMMENDATION_TIERS as readonly string[]).includes(value);
}

function isPackageComplexitySignal(value: string): value is PackageComplexitySignal {
  return (PACKAGE_COMPLEXITY_SIGNALS as readonly string[]).includes(value);
}

function nonEmpty(value: string | undefined): string | null {
  const normalized = value?.trim();

  return normalized && normalized.length > 0 ? normalized : null;
}

function getDerivedComplexitySignals(input: PackageRecommendationInput): readonly PackageComplexitySignal[] {
  const services = input.serviceIds ?? [];
  const addOns = input.addOnIds ?? [];
  const signals: PackageComplexitySignal[] = [];

  if (input.audienceId === "agency") signals.push("agency-audience");
  if (input.audienceId === "adult") signals.push("adult-audience");
  if (input.audienceId === "franchise") signals.push("franchise-audience");
  if (services.includes("white-label") || services.includes("white-label-seo")) {
    signals.push("white-label-service");
  }
  if (services.includes("franchise-local") || services.includes("franchise-multi-location")) {
    signals.push("franchise-local-service");
  }
  if (addOns.includes("compliance")) signals.push("compliance-addon");

  return signals;
}

function getComplexitySignals(input: PackageRecommendationInput): readonly PackageComplexitySignal[] {
  const explicitSignals = (input.complexitySignals ?? []).filter(isPackageComplexitySignal);

  return [...new Set([...getDerivedComplexitySignals(input), ...explicitSignals])];
}

function calculateTier(
  serviceCount: number,
  addOnCount: number,
  complexitySignalCount: number,
): PackageRecommendationTier {
  // These thresholds intentionally match the current PackageConfigurator
  // resolver so a later migration can be behaviour-preserving.
  if (serviceCount >= 4 || addOnCount >= 3 || complexitySignalCount >= 2) {
    return "Enterprise";
  }

  if (serviceCount >= 2 || addOnCount >= 2 || complexitySignalCount === 1) {
    return "Growth";
  }

  return "Starter";
}

function getCalculationReasons(
  tier: PackageRecommendationTier,
  serviceCount: number,
  addOnCount: number,
  complexitySignals: readonly PackageComplexitySignal[],
): readonly PackageRecommendationReason[] {
  const reasons: PackageRecommendationReason[] = [];

  if (tier === "Enterprise") {
    if (serviceCount >= 4) reasons.push("service-count-enterprise");
    if (addOnCount >= 3) reasons.push("addon-count-enterprise");
    if (complexitySignals.length >= 2) reasons.push("complexity-enterprise");
  } else if (tier === "Growth") {
    if (serviceCount >= 2) reasons.push("service-count-growth");
    if (addOnCount >= 2) reasons.push("addon-count-growth");
    if (complexitySignals.length === 1) reasons.push("complexity-growth");
  }

  reasons.push(...complexitySignals);

  return reasons.length > 0 ? reasons : ["baseline-scope"];
}

function getPricingResolution(
  tier: PackageRecommendationTier,
  input: PackageRecommendationInput,
): Pick<PackageRecommendation, "pricingBand" | "pricingSnapshotVersion" | "missingInformation"> {
  const missingInformation: PackageRecommendationMissingInformation[] = [];

  if (!nonEmpty(input.market)) missingInformation.push("market");
  if (!nonEmpty(input.currency)) missingInformation.push("currency");

  const pricingSnapshot = input.pricingSnapshot;
  if (!pricingSnapshot) {
    missingInformation.push("pricing-snapshot");

    return {
      pricingBand: null,
      pricingSnapshotVersion: null,
      missingInformation,
    };
  }

  const pricingSnapshotVersion = nonEmpty(pricingSnapshot.version);
  const pricingBand = nonEmpty(pricingSnapshot.bands[tier]);

  if (!pricingSnapshotVersion) missingInformation.push("pricing-snapshot-version");
  if (!pricingBand) missingInformation.push("pricing-band");

  return {
    pricingBand,
    pricingSnapshotVersion,
    missingInformation,
  };
}

/**
 * Resolves a recommendation without I/O. An incomplete pricing context fails
 * closed to a manual quote requirement while retaining the rule-based tier.
 */
export function resolvePackageRecommendation(input: PackageRecommendationInput): PackageRecommendation {
  const serviceCount = input.serviceIds?.length ?? 0;
  const addOnCount = input.addOnIds?.length ?? 0;
  const complexitySignals = getComplexitySignals(input);
  const calculatedTier = calculateTier(serviceCount, addOnCount, complexitySignals.length);
  const overridden = isPackageRecommendationTier(input.packageOverride);
  const tier = overridden ? input.packageOverride : calculatedTier;
  const calculatedReasons = getCalculationReasons(calculatedTier, serviceCount, addOnCount, complexitySignals);
  const reasons = overridden ? (["package-override", ...calculatedReasons] as const) : calculatedReasons;
  const pricing = getPricingResolution(tier, input);

  return {
    tier,
    reasons,
    pricingBand: pricing.pricingBand,
    pricingSnapshotVersion: pricing.pricingSnapshotVersion,
    ruleVersion: PACKAGE_RECOMMENDATION_RULE_VERSION,
    missingInformation: pricing.missingInformation,
    manualQuoteRequired: pricing.missingInformation.length > 0,
    overridden,
  };
}
